import { CompetitionPledge, CompetitionPledges, CompetitionUserScore, PledgeCompletions } from '../userApi';

export const ELECTRICITY_DOLLARS_PER_KWH = 0.32561;
export const GAS_DOLLARS_PER_THERM = 2.37256;
export const GASOLINE_DOLLARS_PER_GALLON = 5.701;
export const WATER_AND_SEWER_DOLLARS_PER_GALLON = 0.04126;

const GASOLINE_CO2_LB_PER_GALLON = 19.592;
const NATURAL_GAS_CO2_LB_PER_THERM = 11.684;
const CAMX_CO2_LB_PER_KWH = 0.4285;

const transportationActions: { [title: string]: boolean } = {
  'Carpool when commuting to work by car and share how many days you carpooled.': true,
  'Commute to work taking modes other than car (i.e public transit, bike, or walk)': true,
  'Leave your car at home for a week': true
};

const inferredElectricityActions: { [title: string]: boolean } = {
  'Hang dry your clothes': true,
  'Open windows instead of using air conditioning': true,
  'Swap bulbs to LEDs': true,
  'Turn off lights in common areas': true
};

const meteredWaterActions: { [title: string]: boolean } = {
  'Convert to and Install a low-flow showerhead': true,
  'Operate your clothes washer and dishwasher with full loads': true
};

export interface ActionSavingsEstimate {
  electricityKwh?: number;
  naturalGasTherms?: number;
  gasolineGallons?: number;
  waterGallons?: number;
  dollarsSaved?: number;
  basis: string;
  confidence: 'High' | 'Medium' | 'Low';
}

export const estimateActionSavings = (pledge: CompetitionPledge): ActionSavingsEstimate => {
  const title = pledge.shortTitle;
  let electricityKwh = Number(pledge.savingsElectricityKwh || 0);
  let naturalGasTherms = 0;
  let gasolineGallons = 0;
  const waterGallons = Number(pledge.savingsWaterOz || 0);
  let localWaterGallons = 0;
  let confidence: ActionSavingsEstimate['confidence'] = 'Low';
  const basis: string[] = [];

  if (electricityKwh > 0) {
    basis.push('Catalog kWh');
    confidence = 'High';
  } else if (inferredElectricityActions[title]) {
    electricityKwh = Number(pledge.savingsCO2Lbs || 0) / CAMX_CO2_LB_PER_KWH;
    basis.push('CAMX grid-equivalent kWh');
    confidence = 'Medium';
  }

  if (transportationActions[title]) {
    gasolineGallons = Number(pledge.savingsCO2Lbs || 0) / GASOLINE_CO2_LB_PER_GALLON;
    basis.push('EPA gasoline equivalent');
    confidence = 'Medium';
  }

  if (title === 'Wash clothes in cold water') {
    naturalGasTherms = Number(pledge.savingsCO2Lbs || 0) / NATURAL_GAS_CO2_LB_PER_THERM;
    basis.push('EPA natural-gas equivalent');
    confidence = 'Medium';
  }

  if (title === 'Convert to and Install a low-flow showerhead') {
    naturalGasTherms = 1.5 * 10.307;
    localWaterGallons = waterGallons;
    basis.push('Catalog water', 'EPA WaterSense gas');
    confidence = 'High';
  } else if (meteredWaterActions[title]) {
    localWaterGallons = waterGallons;
    basis.push('Catalog metered water');
    confidence = 'High';
  } else if (waterGallons > 0) {
    basis.push('Catalog lifecycle or remote water');
  }

  const dollarsSaved =
    electricityKwh * ELECTRICITY_DOLLARS_PER_KWH
    + naturalGasTherms * GAS_DOLLARS_PER_THERM
    + gasolineGallons * GASOLINE_DOLLARS_PER_GALLON
    + localWaterGallons * WATER_AND_SEWER_DOLLARS_PER_GALLON;
  if (basis.length === 0) {
    basis.push('Lifecycle CO2 only; no defensible billable unit');
  }

  return {
    electricityKwh: electricityKwh || undefined,
    naturalGasTherms: naturalGasTherms || undefined,
    gasolineGallons: gasolineGallons || undefined,
    waterGallons: waterGallons || undefined,
    dollarsSaved: dollarsSaved || undefined,
    basis: basis.join('; '),
    confidence
  };
};

export interface UserDollarSavingsSplit {
  newDollarsSaved: number;
  takenDollarsSaved: number;
  totalDollarsSaved: number;
}

/**
 * Screening estimate of entity $ savings from aggregated CO2, using the catalog's
 * average SF bill $/lb across pledges (includes $0 lifecycle actions in the
 * denominator so food/remote water does not inflate the rate).
 */
export const estimateEntityDollarSavings = (
  score: { savingsCO2Lbs?: number } | undefined,
  pledges: CompetitionPledges | undefined
): number => {
  const entityCO2 = Number((score && score.savingsCO2Lbs) || 0);
  if (entityCO2 <= 0 || !pledges) {
    return 0;
  }
  let catalogDollars = 0;
  let catalogCO2 = 0;
  Object.keys(pledges).forEach((pledgeID) => {
    const pledge = pledges[Number(pledgeID)];
    if (!pledge) return;
    const co2 = Number(pledge.savingsCO2Lbs || 0);
    if (co2 <= 0) return;
    catalogCO2 += co2;
    catalogDollars += Number(estimateActionSavings(pledge).dollarsSaved || 0);
  });
  if (catalogCO2 <= 0) {
    return 0;
  }
  return entityCO2 * (catalogDollars / catalogCO2);
};

/**
 * Estimate personal $ savings from credited completions using SF bill rates.
 * Splits new vs already-taken proportionally to stored CO2 (no $ column in DB).
 */
export const estimateUserDollarSavings = (
  pledges: CompetitionPledges | undefined,
  completions: PledgeCompletions | undefined,
  score: CompetitionUserScore | undefined
): UserDollarSavingsSplit => {
  let totalDollarsSaved = 0;
  if (pledges && completions) {
    Object.keys(completions).forEach((pledgeID) => {
      const count = Number(completions[pledgeID] || 0);
      if (!count) return;
      const pledge = pledges[Number(pledgeID)];
      if (!pledge) return;
      const dollars = Number(estimateActionSavings(pledge).dollarsSaved || 0);
      if (dollars > 0) {
        totalDollarsSaved += dollars * count;
      }
    });
  }

  const newCO2 = Number((score && score.savingsCO2Lbs) || 0);
  const takenCO2 = Number((score && score.takenCO2Lbs) || 0);
  const totalCO2 = newCO2 + takenCO2;
  if (totalDollarsSaved <= 0 || totalCO2 <= 0) {
    return {
      newDollarsSaved: totalDollarsSaved > 0 && takenCO2 <= 0 ? totalDollarsSaved : 0,
      takenDollarsSaved: totalDollarsSaved > 0 && newCO2 <= 0 ? totalDollarsSaved : 0,
      totalDollarsSaved
    };
  }

  const newDollarsSaved = totalDollarsSaved * (newCO2 / totalCO2);
  return {
    newDollarsSaved,
    takenDollarsSaved: totalDollarsSaved - newDollarsSaved,
    totalDollarsSaved
  };
};
